import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaletteIcon from '@mui/icons-material/Palette';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SliderModal } from '@components/molecules/SlideOver';
import { ConfirmationModal } from '@components/molecules/ConfirmationModal';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { FormGroup } from '@components/molecules/form/FormGroup';
import {
  useActiveTaskCategories,
  useCreateTaskCategory,
  useDeleteTaskCategory,
  useTaskCategories,
  useUpdateTaskCategory,
} from '@hooks/api/taskCategory';
import {
  TaskCategory,
  TaskCategoryCreateInput,
  TaskCategoryUpdateInput,
} from '@models/taskCategory.model';
import { useTranslation } from '@hooks/useTranslation';

interface TaskCategoriesSlideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Mode = 'list' | 'form';

const COLOR_OPTIONS = [
  { value: 'blue', label: 'Blue', bg: 'bg-blue-500' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-500' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
  { value: 'cyan', label: 'Cyan', bg: 'bg-cyan-500' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500' },
  { value: 'green', label: 'Green', bg: 'bg-green-500' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500' },
];

// Display map: tolerates the broader palette used by the migration seed so
// existing global categories (red, gray, stone, …) still render with the
// correct icon background instead of falling back to blue.
const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-600',
  purple: 'bg-purple-100 text-purple-600',
  orange: 'bg-orange-100 text-orange-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  green: 'bg-green-100 text-green-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  red: 'bg-red-100 text-red-600',
  pink: 'bg-pink-100 text-pink-600',
  teal: 'bg-teal-100 text-teal-600',
  gray: 'bg-gray-100 text-gray-600',
  slate: 'bg-slate-100 text-slate-600',
  stone: 'bg-stone-100 text-stone-600',
  brown: 'bg-amber-100 text-amber-700',
};

const colorClassesFor = (color?: string) =>
  COLOR_MAP[color || 'blue'] || COLOR_MAP.blue;

const emptyForm = {
  name: '',
  color: 'blue',
  isActive: true,
  sortOrder: '',
};

export const TaskCategoriesSlideModal: React.FC<TaskCategoriesSlideModalProps> = ({
  isOpen,
  onClose,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const [mode, setMode] = useState<Mode>('list');
  const [selected, setSelected] = useState<TaskCategory | null>(null);
  const [toDelete, setToDelete] = useState<TaskCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [nameError, setNameError] = useState('');

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useTaskCategories();
  // Pre-warm the active-categories cache so the task-form dropdown picks up
  // changes immediately after the user creates/updates a category here.
  useActiveTaskCategories();

  const createMutation = useCreateTaskCategory();
  const updateMutation = useUpdateTaskCategory();
  const deleteMutation = useDeleteTaskCategory();

  const isLoading = categoriesLoading && !categoriesError;
  const isMutating = createMutation.isPending || updateMutation.isPending;
  const isEditing = !!selected;

  // Build a lowercase set of global names so we can warn before submitting
  // an org-scoped category that would collide with a global one
  // (mirrors the warehouse CategoryFormModal validation).
  const globalCategoryNames = useMemo(
    () =>
      new Set(
        categories
          .filter((c) => c.isGlobal)
          .map((c) => c.name.trim().toLowerCase()),
      ),
    [categories],
  );

  const validateName = useCallback(
    (value: string): string => {
      const trimmed = value.trim().toLowerCase();
      if (!trimmed) return '';
      // Editing an existing org category: skip self-collision (its name
      // can't be in globals — globals can't be edited from this modal).
      if (selected && !selected.isGlobal && selected.name.trim().toLowerCase() === trimmed) {
        return '';
      }
      if (globalCategoryNames.has(trimmed)) {
        const matched = categories.find(
          (c) => c.isGlobal && c.name.trim().toLowerCase() === trimmed,
        );
        return `"${matched?.name}" already exists as a global category. Use a different name like "${matched?.name} 2".`;
      }
      return '';
    },
    [categories, globalCategoryNames, selected],
  );

  // Reset everything when slide closes
  useEffect(() => {
    if (!isOpen) {
      setMode('list');
      setSelected(null);
      setToDelete(null);
      setSearchQuery('');
      setForm(emptyForm);
      setNameError('');
    }
  }, [isOpen]);

  const filteredCategories = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const list = query
      ? categories.filter((c) => c.name.toLowerCase().includes(query))
      : categories;
    return [...list].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [categories, searchQuery]);

  const activeCount = categories.filter((c) => c.isActive).length;

  const handleAddCategory = useCallback(() => {
    setSelected(null);
    setForm({ ...emptyForm, sortOrder: String(categories.length) });
    setNameError('');
    setMode('form');
  }, [categories.length]);

  const handleEditCategory = useCallback((category: TaskCategory) => {
    setSelected(category);
    setForm({
      name: category.name || '',
      color: category.color || 'blue',
      isActive: category.isActive ?? true,
      sortOrder: category.sortOrder?.toString() || '',
    });
    setNameError('');
    setMode('form');
  }, []);

  const backToList = useCallback(() => {
    setMode('list');
    setSelected(null);
    setForm(emptyForm);
    setNameError('');
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, name: value }));
    setNameError(validateName(value));
  };

  const handleSubmit = useCallback(async () => {
    const trimmed = form.name.trim();
    if (!trimmed) return;

    const error = validateName(form.name);
    if (error) {
      setNameError(error);
      return;
    }

    const baseData = {
      name: trimmed,
      color: form.color || undefined,
      isActive: form.isActive,
      sortOrder: form.sortOrder ? parseInt(form.sortOrder, 10) : undefined,
    };

    try {
      if (isEditing && selected) {
        await updateMutation.mutateAsync({
          taskCategoryId: selected.taskCategoryId,
          ...baseData,
        } as TaskCategoryUpdateInput);
        toast.success(tActionMessages.categoryUpdatedSuccessfully);
      } else {
        await createMutation.mutateAsync(baseData as TaskCategoryCreateInput);
        toast.success(tActionMessages.categoryCreatedSuccessfully);
      }
      backToList();
    } catch (err: any) {
      console.error('Error saving task category:', err);
      toast.error(
        err?.response?.errors?.[0]?.message ||
          err?.message ||
          tActionMessages.failedToSaveCategory,
      );
    }
  }, [
    backToList,
    createMutation,
    form,
    isEditing,
    selected,
    updateMutation,
    validateName,
    tActionMessages.categoryCreatedSuccessfully,
    tActionMessages.categoryUpdatedSuccessfully,
    tActionMessages.failedToSaveCategory,
  ]);

  const handleConfirmDelete = useCallback(async () => {
    if (!toDelete) return;
    try {
      await deleteMutation.mutateAsync(toDelete.taskCategoryId);
      toast.success(tActionMessages.categoryDeletedSuccessfully);
      setToDelete(null);
    } catch (err: any) {
      console.error('Error deleting task category:', err);
      toast.error(
        err?.response?.errors?.[0]?.message ||
          err?.message ||
          tActionMessages.failedToDeleteCategory,
      );
    }
  }, [deleteMutation, toDelete, tActionMessages.categoryDeletedSuccessfully, tActionMessages.failedToDeleteCategory]);

  const title = mode === 'list' ? 'Task Categories' : isEditing ? 'Edit Category' : 'Add Category';

  const actionButtons =
    mode === 'form'
      ? [
          {
            text: isEditing ? 'Save Changes' : 'Add Category',
            onClick: handleSubmit,
            variant: 'primary' as const,
            disabled: isMutating || !form.name.trim() || !!nameError,
          },
        ]
      : undefined;

  return (
    <>
      <SliderModal
        isOpen={isOpen}
        title={title}
        closeSlider={mode === 'form' ? backToList : onClose}
        cancelText={mode === 'form' ? 'Back' : 'Close'}
        actionButtons={actionButtons}
      >
        {mode === 'list' && (
          <div className="space-y-6 pb-24">
            {/* Header with primary action */}
            <div className="flex justify-end">
              <Button
                variant="primary"
                leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleAddCategory}
              >
                Add Category
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
                <CategoryIcon className="text-gray-400" sx={{ fontSize: 24 }} />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
                  <p className="text-xs text-gray-500">Total Categories</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
                <CheckCircleIcon className="text-green-500" sx={{ fontSize: 24 }} />
                <div>
                  <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                  <p className="text-xs text-gray-500">Active</p>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 flex items-center gap-3">
                <CategoryIcon className="text-indigo-500" sx={{ fontSize: 24 }} />
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {categories.length - activeCount}
                  </p>
                  <p className="text-xs text-gray-500">Inactive</p>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className="relative w-64">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                sx={{ fontSize: 18 }}
              />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loading size="3xl" />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Add Category Card */}
                <button type="button"
                  onClick={handleAddCategory}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-indigo-600 hover:border-indigo-300 transition-colors min-h-[140px]"
                >
                  <AddIcon sx={{ fontSize: 24 }} />
                  <span className="text-sm font-medium">Add Category</span>
                </button>

                {filteredCategories.map((category) => {
                  const colorClasses = colorClassesFor(category.color);
                  return (
                    <div
                      key={category.taskCategoryId}
                      className={`bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-4 relative group transition-all hover:shadow-md ${
                        !category.isActive ? 'opacity-60' : ''
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses}`}
                      >
                        <CategoryIcon sx={{ fontSize: 22 }} />
                      </div>

                      {/* Name */}
                      <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>

                      {/* Status & Global badges */}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            category.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {category.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {category.isGlobal && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            <PublicIcon sx={{ fontSize: 12 }} />
                            Global
                          </span>
                        )}
                      </div>

                      {/* Hover Actions (hidden for global categories) */}
                      {!category.isGlobal && (
                        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button type="button"
                            onClick={() => handleEditCategory(category)}
                            className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-indigo-600 hover:ring-indigo-200"
                            title="Edit"
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </button>
                          <button type="button"
                            onClick={() => setToDelete(category)}
                            className="p-1.5 bg-white rounded-md shadow-sm ring-1 ring-gray-200 text-gray-400 hover:text-red-600 hover:ring-red-200"
                            title="Delete"
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {mode === 'form' && (
          <div className="space-y-5 pb-24">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
              onClick={backToList}
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} /> Back to list
            </button>

            {/* Appearance */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PaletteIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
                Appearance
              </h3>

              {/* Preview */}
              <div className="flex items-center gap-4 mb-4">
                <div
                  className={`w-14 h-14 rounded-xl flex items-center justify-center ${colorClassesFor(
                    form.color,
                  )}`}
                >
                  <CategoryIcon sx={{ fontSize: 28 }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Preview</p>
                  <p className="text-xs text-gray-500">
                    {form.name.trim() || 'Category name'} &middot;{' '}
                    {COLOR_OPTIONS.find((o) => o.value === form.color)?.label || 'Blue'}
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex gap-2">
                  {COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, color: opt.value }))}
                      className={`w-8 h-8 rounded-full ${opt.bg} transition-all ${
                        form.color === opt.value
                          ? 'ring-2 ring-offset-2 ring-indigo-500 scale-110'
                          : 'hover:scale-105'
                      }`}
                      title={opt.label}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
                Details
              </h3>

              <div className="flex flex-col gap-4">
                <FormGroup label="Category Name *" htmlFor="taskCategoryName">
                  <Input
                    id="taskCategoryName"
                    value={form.name}
                    onChange={handleNameChange}
                    placeholder="e.g., Drywall"
                    autoFocus
                  />
                  {nameError && <p className="mt-1 text-xs text-red-600">{nameError}</p>}
                </FormGroup>

                <div className="grid grid-cols-2 gap-4">
                  <FormGroup label="Sort Order" htmlFor="taskCategorySortOrder">
                    <Input
                      id="taskCategorySortOrder"
                      type="number"
                      min="0"
                      value={form.sortOrder}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, sortOrder: e.target.value }))
                      }
                      placeholder="0"
                    />
                    <p className="mt-1 text-xs text-gray-500">Lower numbers appear first</p>
                  </FormGroup>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="taskCategoryStatus"
                          checked={form.isActive}
                          onChange={() => setForm((f) => ({ ...f, isActive: true }))}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Active</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="taskCategoryStatus"
                          checked={!form.isActive}
                          onChange={() => setForm((f) => ({ ...f, isActive: false }))}
                          className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-700">Inactive</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </SliderModal>

      <ConfirmationModal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description={`Are you sure you want to delete "${toDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default TaskCategoriesSlideModal;
